/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import { connect } from 'react-redux';
import { MuiBox, MuiDivider, MuiMenuItem, MuiSelect, MuiTypography } from 'theme/material-ui';
import { Grid } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import { KeyboardDatePicker } from '@material-ui/pickers';
import RequiredLabel from 'components/UI/Labels/RequiredLabel';
import FlexBox from 'components/UI/Layout/FlexBox';
import Spacer from 'components/UI/Layout/Spacer';
import Footer from 'components/common/Footer';
import { InfoTooltip } from 'components/InfoTooltip';
import SmallText from 'components/UI/Typography/SmallText';
import { Formik } from 'formik';
import { CovidTitles } from 'lib/constants/covidScreening';
import { getAge } from 'lib/booking/utils';
import dayjs from 'dayjs';
import {
  COVID_SCREENING_LEGAL_SEX_TEXT,
  COVID_SCREENING_PERSONAL_INFO_BODY_1,
  COVID_SCREENING_PERSONAL_INFO_BODY_2,
  COVID_SCREENING_PERSONAL_INFO_BODY_3,
  SEX_OPTIONS,
  SexOptionsType,
  StateOptionsType,
  submitGuestPersonalInfoValidation,
  FormValues
} from 'modules/constants/covidScreening/personalInfo';
import { LOCATION_STATES } from 'modules/constants/LocationStates';
import { Color } from 'modules/styles/colors';
import { FontSize } from 'modules/styles/variables';
import { patientSelect } from 'modules/constants/covidScreening';
import { maskFullDate, maskPhone, maskZip } from 'modules/utils/MaskUtils';
import { NavigationScreenProps } from 'screens/navigation';
import CustomTextField from 'screens/Register/FormElements/CustomTextField';
import * as covidActions from 'store/CovidScreening/actions';
import { CovidScreeningPatientInfoSelector } from 'store/CovidScreening/selectors';
import { Consumer } from 'store/profile/types';
import { RootState } from 'store/types';
import styled from 'styled-components';
import { CovidTestButton } from '../../CovidScreening/Components/CovidTestButton';
import useStyles from '../../CovidScreening/Components/CovidTestButton/useStyles';
import FieldError from 'screens/Register/FormElements/FieldError';

export interface CovidPersonalInfoScreenProps extends NavigationScreenProps {
  selectUser: Consumer;
  setPatient: typeof covidActions.setPatient;
}

export function CovidPersonalInfoScreen({
  setPatient,
  history,
  selectUser
}: CovidPersonalInfoScreenProps) {
  const phone = type => {
    const tPhone = selectUser?.phones?.find(el => el.phoneType === type);
    return tPhone ? tPhone.areaCode + tPhone.phoneNumber : '';
  };

  const homeAddress = selectUser?.addresses?.find(el => el.type === 'HOMEADDR');

  const initialValues = {
    userFirstName: selectUser?.names?.[0]?.givenName || '',
    userMiddleName: selectUser?.names?.[0]?.middleName || '',
    userLastName: selectUser?.names?.[0]?.familyName || '',
    userDateOfBirth: maskFullDate(selectUser?.dateOfBirth) || '',
    userSSNDigits: selectUser?.ssn?.substring(-4),
    userSex: selectUser?.genderType?.toUpperCase() || '',
    userAddressLine1: homeAddress?.line1 || '',
    userAddressLine2: homeAddress?.line2 || '',
    userCity: homeAddress?.city || '',
    userState: homeAddress?.state || '',
    userZip: maskZip(homeAddress?.zip) || '',
    userEmail: selectUser?.emails[0]?.address || '',
    userCellNumber: maskPhone(
      selectUser?.phones?.length
        ? selectUser?.phones[0].areaCode + selectUser?.phones[0].phoneNumber
        : ''
    ),
    userOtherPhoneNumber: selectUser?.alternateNumber || ''
  };
  const classes = useStyles();

  const standardWidth = 'fullwidth';
  const standardHeight = `
height: 42px;
min-height: 42px;`;

  const StyledSelect = styled(MuiSelect)`
    ${standardWidth}
    ${standardHeight}
  select {
      padding: 0px 32px 0px 7px;
      ${standardHeight}
    }
  `;

  const saveToStore = (values: FormValues) => {
    const updatedPatientsData = {
      dateOfBirth: dayjs(values?.userDateOfBirth).format('MM/DD/YYYY'),
      genderType: values?.userSex?.toUpperCase(),
      ssn: values?.userSSNDigits,
      alternateNumber: values?.userOtherPhoneNumber,
      names: [
        {
          givenName: values?.userFirstName,
          familyName: values?.userLastName,
          middleName: values?.userMiddleName
        }
      ],
      addresses: [
        {
          line1: values?.userAddressLine1,
          line2: values?.userAddressLine2,
          city: values?.userCity,
          state: values?.userState,
          zip: values?.userZip,
          type: 'HOMEADDR'
        }
      ],
      phones: [
        {
          areaCode: values?.userCellNumber.replace(/[^a-zA-Z0-9]/g, '').substring(0, 3),
          phoneNumber: values?.userCellNumber.replace(/[^a-zA-Z0-9]/g, '').substring(3, 10),
          countryCode: null,
          phoneType: 'CELLPHONE'
        },
        {
          areaCode: values?.userOtherPhoneNumber.replace(/[^a-zA-Z0-9]/g, '').substring(0, 3),
          phoneNumber: values?.userOtherPhoneNumber.replace(/[^a-zA-Z0-9]/g, '').substring(3, 10),
          countryCode: null,
          phoneType: 'ALTPHONE'
        }
      ],
      emails: [
        {
          address: values?.userEmail,
          type: 'HOMEEMAIL'
        }
      ]
    };
    setPatient(updatedPatientsData);
  };

  const handleSubmit = (values: FormValues) => {
    saveToStore(values);
    history.push('/guest-covid-insurance');
  };

  const onCancelClick = (values: FormValues) => {
    saveToStore(values);
    history.goBack();
  };

  const isValidData =
    initialValues.userFirstName &&
    initialValues.userLastName &&
    initialValues.userDateOfBirth &&
    initialValues.userSex &&
    initialValues.userAddressLine1 &&
    initialValues.userCity &&
    initialValues.userState &&
    initialValues.userZip &&
    initialValues.userEmail &&
    initialValues.userCellNumber;

  return (
    <>
      <Container maxWidth="md">
        <Spacer size="medium" />
        <MuiBox>
          <MuiTypography variant="h4">Personal Information</MuiTypography>
        </MuiBox>
        <Spacer size="small" />
        <MuiBox>
          <MuiDivider />
        </MuiBox>

        <Formik
          enableReinitialize="true"
          validateOnMount
          initialValues={initialValues}
          validationSchema={submitGuestPersonalInfoValidation}
          isInitialValid={isValidData}
        >
          {({
            values,
            isValid,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldTouched,
            setFieldValue
          }) => {
            return (
              <>
                <Spacer size="small" />
                <FlexBox flexDirection="row">
                  <MuiTypography fontSize={FontSize.medium} color={Color.offBlack}>
                    {COVID_SCREENING_PERSONAL_INFO_BODY_1}
                    <b>{COVID_SCREENING_PERSONAL_INFO_BODY_2}</b>
                    {COVID_SCREENING_PERSONAL_INFO_BODY_3}
                  </MuiTypography>
                </FlexBox>
                <Spacer size="medium" />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <RequiredLabel labelText="Legal first name" />
                    <CustomTextField
                      name="userFirstName"
                      variant="outlined"
                      value={values?.userFirstName}
                      onChange={handleChange('userFirstName')}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RequiredLabel hideIndicator labelText="Legal middle name" />
                    <CustomTextField
                      name="userMiddleName"
                      variant="outlined"
                      value={values?.userMiddleName}
                      onChange={handleChange('userMiddleName')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RequiredLabel labelText="Legal last name" />
                    <CustomTextField
                      name="userLastName"
                      variant="outlined"
                      value={values?.userLastName}
                      onChange={handleChange('userLastName')}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RequiredLabel labelText="Date of birth" />
                    <KeyboardDatePicker
                      disableToolbar
                      variant="inline"
                      inputVariant="outlined"
                      format="MM/DD/YYYY"
                      value={values?.userDateOfBirth || null}
                      onChange={value => {
                        setFieldTouched('userDateOfBirth');
                        setFieldValue('userDateOfBirth', value, true);
                      }}
                      onBlur={handleBlur('userDateOfBirth')}
                      maxDate={new Date()}
                      name="userDateOfBirth"
                      data-testid="symptoms-began-date"
                      placeholder="MM/DD/YYYY"
                      required
                      autoOk
                      fullWidth
                      helperText={null}
                      error={touched.userDateOfBirth && errors.userDateOfBirth}
                    />
                    {touched.userDateOfBirth && errors.userDateOfBirth && (
                      <FieldError error={errors.userDateOfBirth} />
                    )}
                    {getAge(values.userDateOfBirth) < 2}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RequiredLabel hideIndicator labelText="Last four digits of SSN" />
                    <CustomTextField
                      data-testid="personal-info-ssn"
                      name="userSSNDigits"
                      variant="outlined"
                      value={values?.userSSNDigits}
                      onChange={handleChange('userSSNDigits')}
                      mask="ssnFourDigits"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FlexBox width="100%">
                      <FlexBox flexDirection="row" justifyContent="flex-start" alignItems="center">
                        <RequiredLabel labelText="Sex" />
                        <InfoTooltip>{COVID_SCREENING_LEGAL_SEX_TEXT}</InfoTooltip>
                      </FlexBox>
                      <StyledSelect
                        value={values.userSex}
                        onChange={handleChange('userSex')}
                        variant="outlined"
                      >
                        {SEX_OPTIONS.map((option: SexOptionsType) => (
                          <MuiMenuItem
                            style={{ whiteSpace: 'normal' }}
                            key={option.label}
                            value={option.value}
                          >
                            {option.label}
                          </MuiMenuItem>
                        ))}
                        {errors?.userSex ? (
                          <SmallText color="red">{errors?.userState}</SmallText>
                        ) : null}
                      </StyledSelect>
                    </FlexBox>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <RequiredLabel labelText="Address line 1" />
                    <CustomTextField
                      name="userAddressLine1"
                      variant="outlined"
                      value={values?.userAddressLine1}
                      onChange={handleChange('userAddressLine1')}
                      onBlur={() => {
                        setFieldTouched('userAddressLine1');
                      }}
                      required
                      maxLength={50}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <RequiredLabel hideIndicator labelText="Address line 2" />
                    <CustomTextField
                      name="userAddressLine2"
                      variant="outlined"
                      value={values?.userAddressLine2}
                      onChange={handleChange('userAddressLine2')}
                      maxLength={50}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RequiredLabel labelText="City" />
                    <CustomTextField
                      name="userCity"
                      variant="outlined"
                      value={values?.userCity}
                      onChange={handleChange('userCity')}
                      onBlur={() => {
                        setFieldTouched('userCity');
                      }}
                      required
                      maxLength={30}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FlexBox width="100%">
                      <RequiredLabel labelText="State" />
                      <StyledSelect
                        variant="outlined"
                        value={values?.userState}
                        onChange={handleChange('userState')}
                      >
                        {LOCATION_STATES.map((option: StateOptionsType) => (
                          <MuiMenuItem
                            style={{ whiteSpace: 'normal' }}
                            key={option.label}
                            value={option.value}
                          >
                            {option.label}
                          </MuiMenuItem>
                        ))}
                      </StyledSelect>
                      {errors?.userState && touched.userState ? (
                        <SmallText color="red">{errors?.userState}</SmallText>
                      ) : null}
                    </FlexBox>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RequiredLabel labelText="Zip" />
                    <CustomTextField
                      name="userZip"
                      variant="outlined"
                      value={values?.userZip}
                      onChange={handleChange('userZip')}
                      mask="zip"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RequiredLabel labelText="Cell phone number" />
                    <CustomTextField
                      name="userCellNumber"
                      variant="outlined"
                      data-testid="cell-phone-number"
                      value={values?.userCellNumber}
                      onChange={handleChange('userCellNumber')}
                      onBlur={() => {
                        setFieldTouched('userCellNumber');
                      }}
                      required
                      mask="phone"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RequiredLabel hideIndicator labelText="Alternate phone number" />
                    <CustomTextField
                      name="userOtherPhoneNumber"
                      variant="outlined"
                      value={values?.userOtherPhoneNumber}
                      data-testid="user-other-phone-number"
                      onChange={handleChange('userOtherPhoneNumber')}
                      mask="phone"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RequiredLabel labelText="Email" />
                    <CustomTextField
                      name="userEmail"
                      variant="outlined"
                      data-testid="user-email"
                      value={values?.userEmail}
                      onChange={handleChange('userEmail')}
                      onBlur={() => {
                        setFieldTouched('userEmail');
                      }}
                      required
                      maxLength={30}
                    />
                  </Grid>
                </Grid>
                <Spacer size="medium" />
                <MuiBox className={classes.steppersFooter}>
                  <CovidTestButton
                    fullWidth
                    data-testid="personal-info-next-btn"
                    color="primary"
                    variant="contained"
                    disabled={!isValid}
                    onClick={() => handleSubmit(values)}
                  >
                    Next
                  </CovidTestButton>
                  <Spacer size="xsmall" />
                  <CovidTestButton
                    variant="text"
                    data-testid="personal-info-previous-button"
                    fullWidth
                    onClick={() => onCancelClick(values)}
                  >
                    Previous
                  </CovidTestButton>
                </MuiBox>
              </>
            );
          }}
        </Formik>
        <Footer
          headerText={patientSelect.GET_PATIENTS.HELPLINE.CovidInfo}
          link={patientSelect.GET_PATIENTS.HELPLINE.CovidPatientSelectHelpline}
          linkName={patientSelect.GET_PATIENTS.HELPLINE.CovidPatientSelectHelpline}
          helplineHours={patientSelect.GET_PATIENTS.HELPLINE.CovidHelplineHours}
          title={CovidTitles.PERSONAL_INFORMATION}
        />
      </Container>
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  selectUser: CovidScreeningPatientInfoSelector(state)
});

const mapDispatchToProps = {
  setPatient: covidActions.setPatient
};

export default connect(mapStateToProps, mapDispatchToProps)(CovidPersonalInfoScreen);
